/**
 * 게시물 상세 보기 컴포넌트
 * 공지사항, 일반자료실, 포토갤러리의 상세 내용을 표시합니다.
 * 첨부파일 다운로드 및 이미지 미리보기 기능을 제공합니다.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { update } from '../Api';
import attachIcon from '../assets/attach.png';
import noticeIcon from '../assets/notice.png';

/**
 * 날짜를 "YYYY. MM. DD" 형식으로 포맷팅하는 함수
 * @param {string|Date} value - 날짜 값
 * @returns {string} 포맷된 날짜 문자열
 */
function formatDate(value) {
  const date = new Date(value);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}. ${month}. ${day}`;
}

/**
 * Item 컴포넌트
 * @param {Object} props
 * @param {Array} props.items - 전체 게시물 배열
 * @param {string} props.no - 현재 게시물 ID
 * @param {string} props.order - 목록 페이지 경로 (예: "/notice", "/data", "/photo")
 */
function Item({ items, no, order }) {
  // 이미지 미리보기 표시 여부 (포토갤러리용)
  const [isdiplay, setDisplay] = useState(false);

  // 현재 표시할 게시물 데이터
  const [item, setItem] = useState();

  /**
   * 파일 URL에서 파일명을 추출하는 함수
   * @param {string} files - 파일 URL
   * @returns {string} 파일명 또는 기본값
   */
  const fileName = (files) => {
    try {
      return files.split('/').at(-1);
    } catch {
      return '파일 이름';
    }
  };

  /**
   * 파일 다운로드 핸들러
   * Blob을 생성하여 파일을 다운로드합니다.
   */
  const handleDownload = () => {
    fetch(item.files, { method: 'GET' })
      .then((res) => {
        return res.blob();
      })
      .then((blob) => {
        // Blob URL 생성
        const url = window.URL.createObjectURL(blob);

        // 임시 a 태그 생성하여 다운로드 트리거
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName(decodeURI(item.files));
        document.body.appendChild(a);
        a.click();

        // 메모리 정리 (60초 후)
        setTimeout((_) => {
          window.URL.revokeObjectURL(url);
        }, 60000);
        a.remove();
      })
      .catch((err) => {
        console.error('err: ', err);
      });
  };

  /**
   * 이미지 미리보기 토글 핸들러
   * 포토갤러리에서 이미지를 표시/숨김합니다.
   */
  const handleDisplay = () => {
    if (isdiplay === false) {
      setDisplay((prevState) => {
        return true;
      });
    } else {
      setDisplay((prevState) => {
        return false;
      });
    }
  };

  /**
   * 컴포넌트 마운트 시 해당 게시물을 찾아 설정하고
   * 조회수를 업데이트합니다.
   */
  useEffect(() => {
    items.map((key) => {
      if (key.id === Number(no)) {
        setItem((prevItem) => key);

        // 조회수 증가 API 호출
        update(key, no);
      }
      return 1;
    });
  }, [items, no]);

  return (
    <Container>
      {item && (
        <>
          {/* 게시물 제목 */}
          <TitleSection>
            <h1>{item.title}</h1>
          </TitleSection>

          {/* 게시물 정보 (작성자, 등록일) */}
          <InfoSection>
            <InfoLabel>작성자</InfoLabel>
            <InfoValue>{item.writer.username}</InfoValue>
            <InfoLabel>등록일</InfoLabel>
            <InfoValue>{formatDate(item.createdAt)}</InfoValue>
          </InfoSection>

          {/* 첨부파일 영역 */}
          <FileSection>
            <FileLabel>첨부파일</FileLabel>
            {item.files && (
              <>
                {/* 포토갤러리가 아닌 경우: 다운로드 버튼 */}
                {order !== '/photo' && (
                  <FileButton onClick={handleDownload}>
                    {fileName(decodeURI(item.files))}
                  </FileButton>
                )}

                {/* 포토갤러리인 경우: 이미지 보기 버튼 */}
                {order === '/photo' && (
                  <FileButton onClick={handleDisplay}>
                    {fileName(decodeURI(item.files))}
                  </FileButton>
                )}
              </>
            )}
            {!item.files && <NoFileText>첨부파일 없음</NoFileText>}
          </FileSection>

          {/* 게시물 내용 */}
          <ContentSection>
            {/* 포토갤러리 이미지 미리보기 (토글) */}
            {isdiplay && (
              <PhotoPreview>
                <img src={decodeURI(item.files)} alt="이미지"></img>
              </PhotoPreview>
            )}

            {/* HTML 콘텐츠 렌더링 */}
            <ContentBody dangerouslySetInnerHTML={{ __html: item.summary }} />
          </ContentSection>

          {/* 목록으로 돌아가기 링크 */}
          <ListLink to={order}>목록보기</ListLink>
        </>
      )}
    </Container>
  );
}

export default Item;

/* Styled Components */

const Container = styled.div`
  position: relative;
  border-top: 3px solid #e5857c;
  width: 100%;
  box-sizing: border-box;
  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    padding: 0 5%;
  }
  * {
    box-sizing: border-box;
  }

  &::after {
    content: '';
    display: block;
    clear: both;
  }
`;

const TitleSection = styled.div`
  /* 데스크톱 기본 스타일 */
  width: 100%;
  box-sizing: border-box;

  h1 {
    font-size: 20px;
    color: #333333;
    padding: 10px 20px;
    border-bottom: 1px solid #c8c8c8;
    margin: 0;
    word-wrap: break-word;
    overflow-wrap: break-word;

    /* 모바일 스타일 */
    @media (max-width: 1023px) {
      font-size: 16px;
      padding: 10px 15px;
    }

    @media (min-width: 768px) and (max-width: 1023px) {
      font-size: 18px;
    }
  }

  &::after {
    content: '';
    display: block;
    clear: both;
  }
`;

const InfoSection = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  padding: 10px 0;
  border-bottom: 1px solid #c8c8c8;
  box-sizing: border-box;

  &::after {
    content: '';
    display: block;
    clear: both;
  }
`;

const InfoLabel = styled.p`
  /* 데스크톱 기본 스타일 */
  font-size: 15px;
  padding: 5px 10px 5px 20px;
  font-weight: bold;
  border-right: 2px solid #c8c8c8;
  margin: 0;
  min-width: 80px;
  flex: 0 0 auto;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    font-size: 13px;
    padding: 5px 5px 5px 15px;
    min-width: 60px;
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    font-size: 14px;
    min-width: 70px;
  }
`;

const InfoValue = styled.p`
  /* 데스크톱 기본 스타일 */
  font-size: 15px;
  padding: 5px 10px;
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  word-wrap: break-word;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    font-size: 13px;
    padding: 5px;
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    font-size: 14px;
  }
`;

const FileSection = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #c8c8c8;
  box-sizing: border-box;

  &::after {
    content: '';
    display: block;
    clear: both;
  }
`;

const FileLabel = styled.p`
  /* 데스크톱 기본 스타일 */
  font-size: 15px;
  padding: 5px 10px 5px 20px;
  font-weight: bold;
  border-right: 2px solid #c8c8c8;
  margin: 0;
  min-width: 80px;
  flex: 0 0 auto;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    font-size: 13px;
    padding: 5px 5px 5px 15px;
    min-width: 60px;
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    font-size: 14px;
    min-width: 70px;
  }
`;

const FileButton = styled.button`
  /* 데스크톱 기본 스타일 */
  margin-left: 10px;
  font-size: 13px;
  height: 30px;
  padding-left: 25px;
  background: url(${attachIcon}) left center no-repeat;
  border: none;
  cursor: pointer;
  background-color: transparent;

  &:hover {
    text-decoration: underline;
  }

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    font-size: 12px;
    padding-left: 20px;
    background-size: 15px 15px;
  }
`;

const NoFileText = styled.p`
  /* 데스크톱 기본 스타일 */
  font-size: 15px;
  padding: 5px 10px;
  margin: 0;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    font-size: 13px;
    padding: 5px;
  }
`;

const ContentSection = styled.div`
  min-height: 250px;
  max-height: 1000px;
  width: 100%;
  padding: 10px 0;
  border-bottom: 3px solid #e5857c;
  overflow: auto;
  box-sizing: border-box;

  &::after {
    content: '';
    display: block;
    clear: both;
  }
`;

const PhotoPreview = styled.div`
  margin: 20px;
  text-align: center;

  img {
    max-width: 100%;
    height: auto;
  }
`;

const ContentBody = styled.div`
  /* 데스크톱 기본 스타일 */
  margin: 50px 20px 20px 20px;
  word-wrap: break-word;
  overflow-wrap: break-word;

  img {
    max-width: 100%;
    height: auto;
  }

  p,
  div,
  span,
  li {
    font-size: 16px;

    /* 모바일 스타일 */
    @media (max-width: 1023px) {
      font-size: 14px;
    }

    @media (min-width: 768px) and (max-width: 1023px) {
      font-size: 15px;
    }
  }

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    margin: 30px 15px 15px 15px;
  }
`;

const ListLink = styled(Link)`
  /* 데스크톱 기본 스타일 */
  float: right;
  margin-top: 10px;
  font-weight: bold;
  font-size: 15px;
  background: url(${noticeIcon}) center center no-repeat;
  background-size: 150px 40px;
  color: #fff;
  padding: 10px 50px;
  text-decoration: none;

  &:hover {
    opacity: 0.9;
  }

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    font-size: 13px;
    background-size: 120px 35px;
    padding: 8px 35px;
  }

  @media (max-width: 425px) {
    font-size: 12px;
    background-size: 100px 30px;
    padding: 6px 25px;
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    font-size: 14px;
    background-size: 140px 38px;
    padding: 9px 45px;
  }
`;
